// frontend/src/components/Connections/ConnectionMappingForm.tsx (v110.0 - Mapeo Inteligente con Selector de Tabla)

import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Box, TextField, Button, Stack, Alert,
  FormControl, Select, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Typography, FormHelperText, InputLabel
} from '@mui/material';
import {
  SchemaDiscoveryPayload, SchemaDiscoveryResponse, FileType
} from '../../types';
import { SYSTEM_FIELDS } from '../../config/systemFields';
import { useMemo, useState, useEffect } from 'react';

// --- Esquema de Validación ---
const mappingSchema = z.object({
  connection_name: z.string().min(1, 'El nombre de la conexión es requerido'),
  mappings: z.record(z.string(), z.string().optional()),
});

type MappingFormValues = z.infer<typeof mappingSchema>;

interface ConnectionMappingFormProps {
  resourceToMap: FileType;
  connectionPayload: SchemaDiscoveryPayload;
  discoveredSchema: SchemaDiscoveryResponse;
  initialConnectionName: string;
  existingMapping: Record<string, string> | undefined;
  onSuccess: (mappingData: Record<string, string>, connectionName: string) => void;
  onBack: () => void;
}

const ConnectionMappingForm = ({ 
  resourceToMap,
  connectionPayload, // Necesario para saber el tipo de conector
  discoveredSchema,
  initialConnectionName,
  existingMapping,
  onSuccess,
  onBack
}: ConnectionMappingFormProps) => {

  const fieldsToMap = SYSTEM_FIELDS[resourceToMap];
  const connectorType = 'connector_type' in connectionPayload ? connectionPayload.connector_type : null;
  const isPostgres = connectorType === 'GENERIC_POSTGRESQL';

  // --- ESTADO PARA SELECCIÓN DE TABLA (SOLO SQL) ---
  // Si es Odoo, no necesitamos seleccionar tabla, el descubrimiento ya nos da las columnas filtradas por recurso.
  // Si es Postgres, discoveredSchema.resources tiene { 'nombre_tabla': ['col1', 'col2'] }
  
  // Intentamos recuperar la tabla seleccionada anteriormente si existe mapping
  const initialTable = existingMapping?.['__table_name__'] || '';
  const initialUpdateCol = existingMapping?.['__last_update_col__'] || '';

  const [selectedTable, setSelectedTable] = useState<string>(initialTable);
  const [lastUpdateColumn, setLastUpdateColumn] = useState<string>(initialUpdateCol);

  const availableTables = useMemo(() => {
    if (!isPostgres) return [];
    return Object.keys(discoveredSchema.resources).sort();
  }, [discoveredSchema, isPostgres]);

  // Obtener las columnas disponibles para desplegables
  const userHeaders = useMemo(() => {
    if (isPostgres) {
        // En Postgres, las columnas dependen de la tabla seleccionada
        if (!selectedTable) return [];
        return discoveredSchema.resources[selectedTable] || [];
    } else {
        // En Odoo, buscamos por clave de recurso (ej: products)
        // Lógica robusta para encontrar las columnas ignorando prefijos si es necesario
        const resourceKey = Object.keys(discoveredSchema.resources).find(
            key => key.includes(resourceToMap)
        );
        return resourceKey ? discoveredSchema.resources[resourceKey] : [];
    }
  }, [discoveredSchema, resourceToMap, isPostgres, selectedTable]);
  
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<MappingFormValues>({
    resolver: zodResolver(mappingSchema),
    defaultValues: {
      connection_name: initialConnectionName || '',
      mappings: existingMapping || {},
    },
    mode: 'onChange',
  });

  // Efecto: Si cambiamos de tabla en Postgres, limpiamos los mapeos seleccionados para evitar inconsistencias
  useEffect(() => {
      if (isPostgres && selectedTable && selectedTable !== initialTable) {
          // Reset mappings ONLY if table changed manually (not on mount)
          // Implementación simple: El usuario tendrá que re-mapear si cambia de tabla.
          // Podríamos ser más sofisticados y mantener columnas que se llamen igual, pero keep it simple.
          fieldsToMap.forEach(field => setValue(`mappings.${field.key}`, ''));
      }
  }, [selectedTable, isPostgres, fieldsToMap, setValue, initialTable]);

  const watchedMappings = useWatch({ control, name: "mappings" });

  const isMappingComplete = useMemo(() => {
    // 1. Validar campos requeridos del sistema
    const requiredFields = fieldsToMap.filter(f => f.required);
    const fieldsMapped = requiredFields.every(f => watchedMappings[f.key]);
    
    // 2. Si es Postgres, validar que se haya seleccionado tabla
    if (isPostgres && !selectedTable) return false;

    return fieldsMapped;
  }, [watchedMappings, fieldsToMap, isPostgres, selectedTable]);

  const onSubmit = (data: MappingFormValues) => {
    // Filtra valores vacíos
    const cleanMappings = Object.fromEntries(
      Object.entries(data.mappings).filter(([, value]) => value)
    ) as Record<string, string>;
    
    // --- INYECCIÓN DE METADATOS SQL ---
    if (isPostgres) {
        cleanMappings['__table_name__'] = selectedTable;
        if (lastUpdateColumn) {
            cleanMappings['__last_update_col__'] = lastUpdateColumn;
        }
    }

    onSuccess(cleanMappings, data.connection_name);
  };

  const isFinalStep = resourceToMap === 'transactions';

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        <Alert severity="info" sx={{ bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#bae6fd' }}>
          {isFinalStep 
            ? "Paso final: Asigne las columnas de sus transacciones. Al finalizar, se guardará la conexión."
            : `Asigne las columnas de sus ${resourceToMap} a los campos requeridos por la aplicación.`
          }
        </Alert>

        <Controller
          name="connection_name"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Nombre para esta Conexión" fullWidth required />
          )}
        />

        {/* --- SELECTOR DE TABLA (SOLO POSTGRES) --- */}
        {isPostgres && (
            <Paper variant="outlined" sx={{ p: 2, borderColor: '#334155', bgcolor: '#1e293b' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#94a3b8' }}>Configuración de Origen SQL</Typography>
                <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="table-select-label">Tabla de Origen ({resourceToMap})</InputLabel>
                        <Select
                            labelId="table-select-label"
                            value={selectedTable}
                            label={`Tabla de Origen (${resourceToMap})`}
                            onChange={(e) => setSelectedTable(e.target.value)}
                        >
                            {availableTables.map(table => (
                                <MenuItem key={table} value={table}>{table}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>Seleccione la tabla de su base de datos que contiene los {resourceToMap}.</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth size="small" disabled={!selectedTable}>
                        <InputLabel id="update-col-label">Columna de Fecha (Incremental - Opcional)</InputLabel>
                        <Select
                            labelId="update-col-label"
                            value={lastUpdateColumn}
                            label="Columna de Fecha (Incremental - Opcional)"
                            onChange={(e) => setLastUpdateColumn(e.target.value)}
                        >
                            <MenuItem value=""><em>Ninguna (Carga Completa siempre)</em></MenuItem>
                            {userHeaders.map(col => (
                                <MenuItem key={col} value={col}>{col}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>Si selecciona una columna de fecha (ej: updated_at), sincronizaremos solo los cambios nuevos.</FormHelperText>
                    </FormControl>
                </Stack>
            </Paper>
        )}

        <Paper variant="outlined" sx={{ borderColor: '#334155', bgcolor: 'transparent' }}>
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#0f172a', color: '#cbd5e1' }}>Campo App ({resourceToMap})</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#0f172a', color: '#cbd5e1' }}>Columna Origen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fieldsToMap.map(field => (
                  <TableRow key={field.key}>
                    <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                      <Typography variant="body2" component="span">{field.label}</Typography>
                      {field.required && <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>*</Typography>}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #334155' }}>
                      <Controller
                        name={`mappings.${field.key}`}
                        control={control}
                        render={({ field: controllerField }) => (
                          <FormControl fullWidth size="small" error={field.required && !controllerField.value}>
                            <Select 
                                {...controllerField} 
                                disabled={isPostgres && !selectedTable} // Deshabilitar si no hay tabla seleccionada
                                displayEmpty
                            >
                              <MenuItem value=""><em>{field.required ? 'Seleccionar...' : 'Opcional'}</em></MenuItem>
                              {userHeaders.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={onBack} variant="outlined" color="secondary">
            Volver
          </Button>
          <Button type="submit" variant="contained" disabled={!isValid || !isMappingComplete} sx={{ flexGrow: 1 }}>
            {isFinalStep ? 'Finalizar y Guardar Conexión' : 'Continuar'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ConnectionMappingForm;